// file: client/src/offline/resolution-code.js
/**
 * Cross-module XWP-25 (refonte WP-41, ux/04 §4.2) — the ONE code resolver answered from the synced
 * snapshot while the server is unreachable (MODULE 09 doctrine: fully functional offline): a
 * Pharmakon lot label (`code_lot` of the stock delta, refonte WP-11) or a box barcode (the
 * produit's `codes_barre[]`, WP-12; every GS1-equivalent form) → the SAME shape as
 * `GET /codes/resolve` (LOT | PRODUIT | AMBIGU | INCONNU), from the same shared modules
 * (`@pharmakon/shared/barcode`, the channel gates and the vocabulary twins). Pure (tested); the
 * server re-checks everything at replay. `hors_ligne: true` marks the answer.
 *
 * The snapshot carries what the counter may see (the sync delta is already role-shaped: a
 * cashier's rows hold sellable units only, `visible_caissier` from the placement authority).
 */
import { analyserCodeLot, analyserCodeProduit, formesEquivalentes, RAISON_CODE_LOT, RAISON_CODE_PRODUIT } from "@pharmakon/shared/barcode";
import { CANAUX_DISPONIBILITE, INDISPONIBILITE, STATUT_PRODUIT_CACHE_CAISSIER } from "@pharmakon/shared/constants/disponibilite-stock.js";
import { CANAL_RESOLUTION_DEFAUT, RAISON_CODE_INCONNU, SELECTION_LOT_COMPTOIR, TYPE_RESOLUTION_CODE } from "@pharmakon/shared/constants/resolution-code.js";
import { estPeremptionSentinelle } from "@/lib/peremption.js";

/** The product gate of `canal` (server `disponibilite.service gateProduit`, same gates). */
export function gateProduitHorsLigne(produit, canal) {
  const g = CANAUX_DISPONIBILITE[canal] ?? CANAUX_DISPONIBILITE[CANAL_RESOLUTION_DEFAUT];
  if (g.statuts_vendables && !g.statuts_vendables.includes(produit.statut_produit)) return INDISPONIBILITE.PRODUIT_BLOQUE;
  if (g.bloque_vente_libre && produit.bloque_vente_libre) return INDISPONIBILITE.VENTE_LIBRE_INTERDITE;
  return null;
}

/** Start of the local day: a lot stays sellable on its expiry day. */
const debutJour = (maintenant) => new Date(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate());

function perime(lot, maintenant) {
  if (!lot.date_peremption || estPeremptionSentinelle(lot.date_peremption)) return false;
  return new Date(lot.date_peremption) < debutJour(maintenant);
}

const vendableDe = (lot, maintenant) => (perime(lot, maintenant) ? 0 : Math.max(0, Number(lot.vendable ?? lot.quantite_disponible ?? 0) || 0));
const visible = (lot) => lot.visible_caissier ?? (lot.magasin_id == null || lot.magasin?.visible_caissier !== false);
const ordreFefo = (a, b) => new Date(a.date_peremption) - new Date(b.date_peremption) || String(a.id).localeCompare(String(b.id));
/** X XWP-95 / XWP-96 — the counter order: the default magasin's lots first (stock delta), then FEFO. */
const ordreComptoir = (a, b) => Number(b.dans_magasin_defaut === true) - Number(a.dans_magasin_defaut === true) || ordreFefo(a, b);

function vueLot(l, maintenant) {
  return {
    id: l.id,
    numero_lot: l.numero_lot ?? null,
    date_peremption: l.date_peremption,
    sans_peremption: estPeremptionSentinelle(l.date_peremption),
    prix_vente_ppa: l.prix_vente_ppa ?? null,
    magasin: l.magasin_id ? { id: l.magasin_id, nom: l.magasin?.nom ?? null } : null,
    magasin_visible: visible(l),
    emplacement: l.emplacement ?? null,
    dans_magasin_defaut: l.dans_magasin_defaut === true,
    perime: perime(l, maintenant),
    vendable: vendableDe(l, maintenant),
  };
}

function vueProduit(p) {
  return {
    id: p.id,
    nom_commercial: p.nom_commercial,
    type_produit: p.type_produit ?? null,
    dci: p.dci?.nom ?? null,
    forme: p.forme?.libelle ?? null,
    dosage: p.dosage ?? null,
    conditionnement: p.conditionnement ?? null,
    laboratoire: p.laboratoire?.nom ?? null,
    ppa: p.ppa ?? null,
    code_barre: p.code_barre ?? null,
    statut_produit: p.statut_produit,
    bloque_vente_libre: p.bloque_vente_libre === true,
    is_psychotrope: p.is_psychotrope === true,
    is_remboursable: p.is_remboursable === true,
    num_enr_cnas: p.num_enr_cnas ?? null,
    tarif_ref: p.tarif_ref ?? null,
    bloque_vente_libre_motif: p.bloque_vente_libre_motif ?? null,
    alerte_psychotrope: p.alerte_psychotrope ?? null,
  };
}

const vivant = (r) => r && !r.deleted_at;
const cacheAuCaissier = (p) => p.statut_produit === STATUT_PRODUIT_CACHE_CAISSIER;

/** Step 1 — the lot label: the rows carrying it, the counter's row (visible first), the next FEFO lot. */
function resoudreLot({ code, produits, lots, canal, maintenant }) {
  const duCode = lots.filter((l) => vivant(l) && l.code_lot === code).sort(ordreFefo);
  if (!duCode.length) return null;
  const produit = produits.find((p) => p.id === duCode[0].produit_id);
  if (!vivant(produit) || cacheAuCaissier(produit)) return null;
  const vues = duCode.map((l) => vueLot(l, maintenant));
  const lot = vues.find((l) => l.vendable > 0 && l.magasin_visible) ?? vues.find((l) => l.vendable > 0) ?? vues.find((l) => l.magasin_visible) ?? vues[0];
  const indisponibilite = gateProduitHorsLigne(produit, canal);
  const quantite_vendable = vues.reduce((s, l) => s + l.vendable, 0);
  const vendable = !indisponibilite && lot.vendable > 0;
  let autre_lot_fefo = null;
  if (!vendable && !indisponibilite) {
    const autres = lots
      .filter((l) => vivant(l) && l.produit_id === produit.id && l.code_lot !== code)
      .sort(ordreFefo)
      .map((l) => ({ ...vueLot(l, maintenant), code_lot: l.code_lot ?? null }));
    autre_lot_fefo = autres.find((a) => a.vendable > 0 && a.magasin_visible) ?? autres.find((a) => a.vendable > 0) ?? null;
  }
  return {
    kind: TYPE_RESOLUTION_CODE.LOT,
    code,
    canal,
    code_lot: { code, numero_lot: lot.numero_lot, date_peremption: lot.date_peremption, sans_peremption: lot.sans_peremption },
    produit: vueProduit(produit),
    lot,
    lots: vues,
    vendabilite: {
      vendable,
      quantite_vendable,
      perime: lot.perime,
      qte_zero: !lot.perime && !indisponibilite && quantite_vendable === 0,
      bloque: indisponibilite != null,
      indisponibilite,
      magasin_visible: lot.magasin_visible,
      autre_lot_fefo,
    },
  };
}

/** Step 2 — the box barcode: every product carrying one of its GS1-equivalent forms. */
function resoudreProduits({ texte, produits, lots, canal, maintenant }) {
  const analyse = analyserCodeProduit(texte);
  const formes = new Set(analyse.valide ? formesEquivalentes(analyse.code, analyse.symbologie) : [String(texte ?? "").replace(/\s+/g, "")]);
  const porteurs = produits
    .filter((p) => vivant(p) && !cacheAuCaissier(p))
    .map((p) => {
      const codes = (p.codes_barre?.length ? p.codes_barre : p.code_barre ? [{ code: p.code_barre, symbologie: null, is_principal: true }] : []);
      const code = codes.find((c) => formes.has(c.code));
      return code ? { produit: p, code } : null;
    })
    .filter(Boolean)
    .sort((a, b) => String(a.produit.nom_commercial).localeCompare(String(b.produit.nom_commercial)));
  const vues = porteurs.map(({ produit, code }) => {
    const sesLots = lots.filter((l) => vivant(l) && l.produit_id === produit.id).sort(ordreComptoir).map((l) => ({ ...vueLot(l, maintenant), code_lot: l.code_lot ?? null }));
    const indisponibilite = gateProduitHorsLigne(produit, canal);
    const quantite_vendable = indisponibilite ? 0 : sesLots.reduce((s, l) => s + l.vendable, 0);
    // XWP-25 / XWP-95 — the counter lots (default magasin first, then FEFO) holding sellable units;
    // the first one is the lot a caisse window holds first
    const lotsComptoir = indisponibilite ? [] : sesLots.filter((l) => l.vendable > 0);
    const lot_fefo = lotsComptoir[0] ?? null;
    // X XWP-96 — offline the caissier's choice setting is not known: AUTO or RUPTURE (the server
    // re-checks the lot at replay)
    const selection_lot = indisponibilite ? null : lot_fefo ? SELECTION_LOT_COMPTOIR.AUTO : SELECTION_LOT_COMPTOIR.RUPTURE;
    return {
      ...vueProduit(produit),
      code_scanne: { code: code.code, symbologie: code.symbologie ?? null, is_principal: code.is_principal === true },
      lot_fefo,
      lots: lotsComptoir,
      selection_lot,
      choix_lot_requis: false,
      vendabilite: {
        vendable: lot_fefo != null,
        quantite_vendable,
        lots_vendables: indisponibilite ? 0 : sesLots.filter((l) => l.vendable > 0).length,
        rupture: !indisponibilite && quantite_vendable === 0,
        bloque: indisponibilite != null,
        indisponibilite,
        magasin_visible: lot_fefo?.magasin_visible ?? true,
      },
    };
  });
  vues.sort((a, b) => Number(b.vendabilite.vendable) - Number(a.vendabilite.vendable));
  return { analyse, produits: vues };
}

/**
 * @param {{ code: string, canal?: string, produits: object[], lots: object[], maintenant?: Date }} p
 *   `produits` / `lots` — the snapshot stores `ref_produits` / `ref_stock`
 */
export function resoudreCodeHorsLigne({ code, canal = CANAL_RESOLUTION_DEFAUT, produits, lots, maintenant = new Date() }) {
  const texte = String(code ?? "").trim();
  const analyseLot = analyserCodeLot(texte);
  const ctx = { produits: produits ?? [], lots: lots ?? [], canal, maintenant };
  const lot = analyseLot.valide ? resoudreLot({ ...ctx, code: analyseLot.code }) : null;
  const { analyse, produits: porteurs } = resoudreProduits({ ...ctx, texte });
  const hors_ligne = true;
  if (lot && porteurs.length > 0) {
    const { kind: _k, code: _c, canal: _canal, ...corps } = lot;
    return { kind: TYPE_RESOLUTION_CODE.AMBIGU, code: lot.code, canal, lot: corps, produits: porteurs, hors_ligne };
  }
  if (lot) return { ...lot, hors_ligne };
  if (porteurs.length > 0) {
    return {
      kind: TYPE_RESOLUTION_CODE.PRODUIT,
      code: analyse.valide ? analyse.code : texte,
      canal,
      symbologie: analyse.valide ? analyse.symbologie : null,
      gtin: analyse.valide ? analyse.gtin : null,
      choix_requis: porteurs.length > 1,
      produits: porteurs,
      hors_ligne,
    };
  }
  const misread = analyseLot.raison === RAISON_CODE_LOT.CLE_INVALIDE || (!analyse.valide && analyse.raison === RAISON_CODE_PRODUIT.CLE_INVALIDE);
  return {
    kind: TYPE_RESOLUTION_CODE.INCONNU,
    code: analyse.valide ? analyse.code : texte,
    raison: misread ? RAISON_CODE_INCONNU.CLE_INVALIDE : RAISON_CODE_INCONNU.INTROUVABLE,
    hors_ligne,
  };
}
